import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePrevious } from 'bxcommon/hooks/usePrevious';
import { BaseLoaderSpinner } from 'bxcommon';
import UserAccountConfirmation from 'bxcommon/components/UserAccountConfirmation/UserAccountConfirmation';
import UserRegistrationConfirmation from 'bxcommon/components/UserRegistrationConfirmation/UserRegistrationConfirmation';
import UserForgotPassword from 'bxcommon/components/UserForgotPassword/UserForgotPassword';
import UserResetPassword from 'bxcommon/components/UserResetPassword/UserResetPassword';
import DocumentDownload from 'bxcommon/components/DocumentDownload/DocumentDownload';
import PrivateRoutes from 'bxcommon/components/RouteWrappers/PrivateRoutes';
import UnauthenticatedRoutes from 'bxcommon/components/RouteWrappers/UnauthenticatedRoutes';
import { RequestProvider } from 'bxcommon/context/RequestProvider';
import { useRequestList } from 'bxcommon/context/RequestList.context';
import { initExistingToken } from 'bxcommon/store/slices/user.slice';
import { warningToast } from 'bxcommon/store/slices/toaster.slice';
import { api } from 'bxcommon/store/api';
import authHelper from 'bxcommon/helpers/auth.helper';
import AppConfig from 'bxcommon/providers/AppConfig';

import AdminLogin from './components/AdminLogin/AdminLogin';
import ListView from './components/ListView/ListView';
import DashboardView from './components/DashboardView/DashboardView';
import RequestDetails from './components/RequestDetails/RequestDetails';
import RequestForm from './components/RequestForm/RequestForm';
import UserLogin from './components/UserLogin/UserLogin';
import UserRegistration from './components/UserRegistration/UserRegistration';
import ThankYouAdvisor from './components/ThankYou/ThankYou';
import PublicRegistry from './components/PublicRegistry/PublicRegistry';

export const RouteList = () => {
  const { t: common, i18n } = useTranslation('common');

  const dispatch = useDispatch();
  const resources = useSelector((state) => state.resources);
  const request = useSelector((state) => state.request);
  const meta = useSelector((state) => state.requestMeta);
  const { list } = useRequestList();
  const user = useSelector((state) => state.user);

  const location = useLocation();

  const [loaded, setLoaded] = useState(false);
  const [redirectTo, setRedirectTo] = useState('');
  const previousLocation = usePrevious(location.pathname);

  useEffect(() => {
    const init = async () => {
      // Things need to be loaded for app to work
      if (!Object.keys(resources).length) {
        try {
          await dispatch(api.endpoints.getResources.initiate({ metadataUrl: '/client-advisors/metadata/', fetchLanguages: true }));

          // if we have token and it's not expired set login action to true (create login only action)
          if (!user.loggedIn && !authHelper.isExpired) {
            await dispatch(initExistingToken());

            if (!user.details.is_admin && list.length) {
              await dispatch({ type: 'request/updateRequestField', payload: list[0] });
            }
          } else if (authHelper.token && authHelper.isExpired) {
            warningToast(common('errors.sessionExpired'), common('errors.sessionExpiredTitle'));
            authHelper.removeToken();
          }
        } finally {
          setLoaded(true);
        }
      }
    };

    init();
  }, [common, dispatch, list, location.pathname, resources, user.details.is_admin, user.loggedIn]);

  useEffect(() => {
    const reloadResources = async () => {
      await dispatch(api.endpoints.getResources.initiate({ metadataUrl: '/client-advisors/metadata/', fetchLanguages: true }));
    };
    reloadResources();
  }, [i18n.language, dispatch]);

  useEffect(() => {
    const initRedirectCheck =
      meta.requestCompleted || (request.case_id && !AppConfig.editStatuses.includes(request.status));

    if (location.pathname === '/confirm-account') {
      authHelper.removeToken();
    }

    let newLocation = '/request-form';
    if (user.details.is_admin) {
      newLocation = '/dashboard';
    } else if (initRedirectCheck) {
      newLocation = '/request-details';
    }
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setRedirectTo(newLocation);
  }, [location.pathname, meta.requestCompleted, request.case_id, request.status, user.details.is_admin]);

  useEffect(() => {
    if (location.pathname !== previousLocation) {
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  });

  const deriveRedirectTo = (currentRedirectTo) => {
    if (location.pathname === '/confirm-account') {
      authHelper.removeToken();
      return;
    }

    const initRedirectCheck =
      meta.requestCompleted || (request.case_id && !AppConfig.editStatuses.includes(request.status));

    let redirectTo = '/request-form';
    if (user.details.is_admin) {
      redirectTo = '/dashboard';
    } else if (initRedirectCheck) {
      redirectTo = '/request-details';
    }

    if (currentRedirectTo !== redirectTo) {
      setRedirectTo(redirectTo);
    }
  };

  deriveRedirectTo(redirectTo);

  return loaded ? (
    <RequestProvider url="client-advisors">
      <Routes>
        <Route element={<PrivateRoutes />}>
          <Route path="/dashboard" element={<DashboardView />} />
          <Route path="/list" element={<ListView />} />
          <Route path="/request-details/:caseId?" element={<RequestDetails />} />
          <Route path="/request-form/confirmation/:caseId?" element={<ThankYouAdvisor />} />
          <Route path="/request-form/:caseId?" element={<RequestForm />} />
          <Route path="/documents/:uuid" element={<DocumentDownload />} />
        </Route>
        <Route element={<UnauthenticatedRoutes />}>
          <Route path="/register/confirmation" element={UserRegistrationConfirmation} />
          <Route path="/register" element={<UserRegistration />} />
          <Route path="/login" element={<UserLogin />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<UserForgotPassword isAdvisor={true} />} />
          <Route path="/reset-password" element={<UserResetPassword isAdvisor={true} />} />
          <Route path="/confirm-account" element={<UserAccountConfirmation />} />
        </Route>

        <Route path="/public-registry" element={<PublicRegistry />} />
        <Route path="*" element={<Navigate to={redirectTo} replace />} />
      </Routes>
    </RequestProvider>
  ) : (
    <BaseLoaderSpinner large />
  );
};

export default RouteList;
